/**
 * Trainova AI — LLM Execution & Token Audit Trail Page
 *
 * Provides real-time visibility into all LLM prompt invocations, token consumptions,
 * models used, latencies, costs, and raw request-response payloads per attempt and scenario.
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { api } from '../../../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { AdminPageHeader } from '../components/AdminPageHeader';
import '../admin-portal.css';

export interface LLMAuditRecord {
  id: string;
  tenantId: string;
  tenantName?: string;
  actorId?: string;
  actorEmail?: string;
  actorName?: string;
  actorRole?: string;
  learnerOutcome?: 'PASSED' | 'NEEDS_IMPROVEMENT' | string;
  purpose: 'SIMULATION_DIALOGUE' | 'SIMULATION_EVALUATION' | 'SCENARIO_CREATION' | 'BEST_CASE_SCRIPT' | 'REALTIME_ASSIST' | 'CALIBRATION_ANALYSIS' | string;
  scenarioId?: string;
  scenarioTitle?: string;
  attemptId?: string;
  turnNumber?: number;
  provider: string;
  model: string;
  prompt: string;
  response: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  latencyMs: number;
  estimatedCost: number;
  status: 'SUCCESS' | 'FAILED' | 'RETRY';
  errorCode?: string | null;
  temperature?: number;
  createdAt: string;
}

export interface LLMAuditSummary {
  totalCalls: number;
  successfulCalls: number;
  failedCalls: number;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
  totalEstimatedCost: number;
  avgLatencyMs: number;
  purposeBreakdown: Record<string, { count: number; tokens: number; cost: number }>;
  modelBreakdown: Record<string, { count: number; tokens: number; cost: number }>;
  simulationBreakdown?: Record<string, { scenarioId: string; scenarioTitle: string; count: number; tokens: number; cost: number }>;
  tenantBreakdown?: Record<string, { tenantId: string; tenantName: string; count: number; tokens: number; cost: number }>;
  attemptBreakdown?: Record<string, { attemptId: string; scenarioTitle: string; count: number; tokens: number; cost: number }>;
  actorBreakdown?: Record<string, { actorId: string; actorName: string; actorEmail?: string; actorRole?: string; count: number; tokens: number; cost: number }>;
}

const toLocalYMD = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const formatFilterDate = (ymd: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!match) return ymd;
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getActorInitials = (name?: string, email?: string): string => {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  if (email && email.trim()) {
    return email.slice(0, 2).toUpperCase();
  }
  return 'LN';
};

const getPromptSnippet = (prompt: any): string => {
  if (!prompt) return '—';
  const str = typeof prompt === 'string' ? prompt : JSON.stringify(prompt);
  if (str.includes('YOU ARE:')) {
    const m = str.match(/YOU ARE:\s*(.*?)(?=\nYOUR ROLE:|$)/s);
    if (m && m[1]) return `Roleplay Persona: ${m[1].trim()}`;
  }
  if (str.includes('[EVALUATION PROMPT')) {
    return 'Evaluation Rubric & Transcript Context';
  }
  const clean = str.replace(/^[#\s]+/, '').replace(/\s+/g, ' ').trim();
  return clean.slice(0, 80);
};

const getResponseSnippet = (response: any): string => {
  if (!response) return '—';
  if (typeof response === 'object') {
    if (response.overallScore !== undefined) {
      return `Score: ${response.overallScore}% (${response.isPassed ? 'Passed' : 'Needs Review'})`;
    }
    if (response.strengths || response.feedback) {
      return `Evaluation Feedback: ${response.feedback || 'Scored'}`;
    }
    return JSON.stringify(response).slice(0, 80);
  }
  const str = String(response);
  try {
    const trimmed = str.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      const obj = JSON.parse(trimmed);
      if (obj.overallScore !== undefined) {
        return `Score: ${obj.overallScore}% (${obj.isPassed ? 'Passed' : 'Needs Review'})`;
      }
      if (obj.strengths || obj.feedback) {
        return `Evaluation Feedback: ${obj.feedback || 'Scored'}`;
      }
    }
  } catch { }
  const clean = str.replace(/%%END%%/g, ' [Exit %%END%%]').replace(/\s+/g, ' ').trim();
  return clean.slice(0, 80);
};

export const LlmAuditTrailPage: React.FC = () => {
  const { isPlatformAdmin } = usePermissions();

  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [logs, setLogs] = useState<LLMAuditRecord[]>([]);
  const [summary, setSummary] = useState<LLMAuditSummary | null>(null);
  const [selectedLog, setSelectedLog] = useState<LLMAuditRecord | null>(null);

  // Filters & Breakdown Controls
  const [selectedTenantId, setSelectedTenantId] = useState<string>('all');
  const [purposeFilter, setPurposeFilter] = useState<string>('all');
  const [modelFilter, setModelFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [simulationFilter, setSimulationFilter] = useState<string>('all');
  const [actorFilter, setActorFilter] = useState<string>('all');
  const [attemptFilter, setAttemptFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Date Range Filtering — the trail opens scoped to today. An unfiltered
  // all-time scan on first load is slow and rarely what an auditor wants.
  const [initialDay] = useState<string>(() => toLocalYMD(new Date()));
  const [datePreset, setDatePreset] = useState<'all' | 'today' | '7d' | '30d' | 'custom'>('today');
  const [startDate, setStartDate] = useState<string>(initialDay);
  const [endDate, setEndDate] = useState<string>(initialDay);

  // Pagination & Enterprise Data Table
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [jumpPage, setJumpPage] = useState<string>('');
  const [activeBreakdownTab, setActiveBreakdownTab] = useState<'simulations' | 'tenants' | 'attempts'>('simulations');

  // Available Tenants for Super Admin
  const [tenants, setTenants] = useState<Array<{ id: string; name: string; slug: string }>>([]);
  const [isTenantDropdownOpen, setIsTenantDropdownOpen] = useState(false);
  const [tenantSearchText, setTenantSearchText] = useState('');
  const tenantDropdownRef = useRef<HTMLDivElement>(null);

  // Copy states
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [copiedResponse, setCopiedResponse] = useState(false);

  // Debounce & in-flight tracking to prevent multiple duplicate requests
  const inFlightRef = useRef(false);
  const debounceTimerRef = useRef<any>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (tenantDropdownRef.current && !tenantDropdownRef.current.contains(event.target as Node)) {
        setIsTenantDropdownOpen(false);
      }
    }
    if (isTenantDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTenantDropdownOpen]);

  // Handle Date Presets — date-only values stay in the auditor's local day
  const handleDatePreset = (preset: 'all' | 'today' | '7d' | '30d' | 'custom') => {
    setDatePreset(preset);
    setCurrentPage(1);

    if (preset === 'all') {
      setStartDate('');
      setEndDate('');
      return;
    }

    const today = new Date();
    const daysAgo = (days: number) => {
      const d = new Date(today);
      d.setDate(d.getDate() - days);
      return d;
    };

    if (preset === 'today') {
      const todayYMD = toLocalYMD(today);
      setStartDate(todayYMD);
      setEndDate(todayYMD);
    } else if (preset === '7d') {
      setStartDate(toLocalYMD(daysAgo(6)));
      setEndDate(toLocalYMD(today));
    } else if (preset === '30d') {
      setStartDate(toLocalYMD(daysAgo(29)));
      setEndDate(toLocalYMD(today));
    }
  };

  // Picking a date loads exactly that day: "From" always aligns "To" to the
  // same day, so a fresh selection never silently widens into a range.
  const handleStartDateChange = (value: string) => {
    setDatePreset('custom');
    setCurrentPage(1);
    setStartDate(value);
    if (value) setEndDate(value);
  };

  const handleEndDateChange = (value: string) => {
    setDatePreset('custom');
    setCurrentPage(1);
    setEndDate(value);
    if (value && (!startDate || value < startDate)) setStartDate(value);
  };

  const dateFilterLabel = useMemo(() => {
    if (startDate && endDate) {
      return startDate === endDate
        ? formatFilterDate(startDate)
        : `${formatFilterDate(startDate)} → ${formatFilterDate(endDate)}`;
    }
    if (startDate) return `From ${formatFilterDate(startDate)}`;
    if (endDate) return `Until ${formatFilterDate(endDate)}`;
    return 'All Time';
  }, [startDate, endDate]);

  // Load Tenants for Super Admin (silently handle 403 for non-platform admins)
  useEffect(() => {
    if (isPlatformAdmin) {
      api.admin.tenants.list()
        .then((res: any) => {
          if (res?.data?.tenants && Array.isArray(res.data.tenants)) {
            setTenants(res.data.tenants);
          } else if (Array.isArray(res?.data)) {
            setTenants(res.data);
          } else if (Array.isArray(res)) {
            setTenants(res);
          }
        })
        .catch(() => {
          setTenants([]);
        });
    }
  }, [isPlatformAdmin]);

  // Fetch Audit Logs — Single unified fetcher with debouncing to eliminate duplicate requests
  const fetchAuditLogs = useCallback(async () => {
    setIsLoading(true);
    inFlightRef.current = true;
    try {
      const queryParams: Record<string, any> = {
        page: currentPage,
        limit: pageSize,
      };
      if (selectedTenantId && selectedTenantId !== 'all') queryParams.tenantId = selectedTenantId;
      if (purposeFilter && purposeFilter !== 'all') queryParams.purpose = purposeFilter;
      if (modelFilter && modelFilter !== 'all') queryParams.model = modelFilter;
      if (statusFilter && statusFilter !== 'all') queryParams.status = statusFilter;
      if (simulationFilter && simulationFilter !== 'all') queryParams.scenarioId = simulationFilter;
      if (actorFilter && actorFilter !== 'all') queryParams.actorId = actorFilter;
      if (startDate) queryParams.startDate = startDate;
      if (endDate) queryParams.endDate = endDate;
      if (attemptFilter.trim()) queryParams.attemptId = attemptFilter.trim();
      if (searchQuery.trim()) queryParams.search = searchQuery.trim();

      const res: any = typeof api.admin?.getLlmAuditLogs === 'function'
        ? await api.admin.getLlmAuditLogs(queryParams)
        : await api.get(`/api/v1/admin/llm-audit`, queryParams);

      // Support direct unwrapping, nested data, and standard ApiResponse shapes
      const rootData: any = res?.data?.data ?? res?.data ?? res;
      const logsArray: LLMAuditRecord[] = Array.isArray(rootData?.logs)
        ? rootData.logs
        : Array.isArray(rootData)
        ? rootData
        : Array.isArray(res?.logs)
        ? res.logs
        : Array.isArray(res?.data)
        ? res.data
        : [];
      const summaryData = rootData?.summary || res?.data?.summary || res?.summary || null;
      const paginationData = rootData?.pagination || res?.data?.pagination || res?.pagination || null;

      setLogs(logsArray);
      setFetchError(null);
      if (summaryData) {
        setSummary(summaryData);
      }
      if (paginationData) {
        setTotalPages(paginationData.totalPages || 1);
        setTotalCount(paginationData.total ?? logsArray.length);
      } else {
        setTotalCount(logsArray.length);
      }
    } catch (err: any) {
      // Surface the failure: silently keeping stale rows makes a date change
      // look like it was ignored.
      setFetchError(err?.message || 'Failed to load LLM audit logs. Please retry.');
      console.warn('[LlmAuditTrailPage] Audit log fetch note:', err?.message || err);
    } finally {
      setIsLoading(false);
      inFlightRef.current = false;
    }
  }, [selectedTenantId, purposeFilter, modelFilter, statusFilter, simulationFilter, actorFilter, startDate, endDate, attemptFilter, searchQuery, currentPage, pageSize]);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      fetchAuditLogs();
    }, 150);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [fetchAuditLogs]);

  const filteredTenantsList = useMemo(() => {
    if (!tenantSearchText) return tenants;
    return tenants.filter(
      (t) =>
        t.name.toLowerCase().includes(tenantSearchText.toLowerCase()) ||
        t.slug.toLowerCase().includes(tenantSearchText.toLowerCase())
    );
  }, [tenants, tenantSearchText]);

  const activeTenantName = useMemo(() => {
    if (selectedTenantId === 'all') return 'All Client Organizations';
    const found = tenants.find((t) => t.id === selectedTenantId);
    return found ? `${found.name} (${found.slug})` : selectedTenantId;
  }, [selectedTenantId, tenants]);

  const availableActors = useMemo(() => {
    const list: Array<{ id: string; name: string; email?: string; role?: string; count: number }> = [];
    if (summary?.actorBreakdown) {
      Object.values(summary.actorBreakdown).forEach((item) => {
        list.push({
          id: item.actorId,
          name: item.actorName || item.actorEmail || item.actorId,
          email: item.actorEmail,
          role: item.actorRole,
          count: item.count,
        });
      });
    }
    return list;
  }, [summary]);

  const paginationPages = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | string)[] = [];
    if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push('...');
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push('...');
      pages.push(currentPage - 1);
      pages.push(currentPage);
      pages.push(currentPage + 1);
      pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  }, [currentPage, totalPages]);

  const availableSimulations = useMemo(() => {
    const list: Array<{ id: string; title: string }> = [
      { id: 'sim_customer_deescalation', title: 'Difficult Customer De-escalation' },
      { id: 'sim_sales_discovery', title: 'Enterprise B2B Sales Discovery' },
      { id: 'sim_leadership_coaching', title: 'Executive Leadership & Performance Feedback' },
    ];
    if (summary?.simulationBreakdown) {
      Object.values(summary.simulationBreakdown).forEach((item) => {
        if (!list.some((s) => s.id === item.scenarioId)) {
          list.push({ id: item.scenarioId, title: item.scenarioTitle || item.scenarioId });
        }
      });
    }
    return list;
  }, [summary]);

  const getPurposeBadge = (purpose: string) => {
    if (!purpose) {
      return { label: 'General Blueprint', color: '#374151', bg: '#F3F4F6', icon: '🤖' };
    }
    switch (purpose) {
      case 'SIMULATION_DIALOGUE':
        return { label: 'Simulation Dialogue', color: '#1E40AF', bg: '#DBEAFE', icon: '💬' };
      case 'SIMULATION_EVALUATION':
        return { label: 'Scoring & Evaluation', color: '#065F46', bg: '#D1FAE5', icon: '⚖️' };
      case 'SCENARIO_CREATION':
        return { label: 'Scenario Generator', color: '#5B21B6', bg: '#EDE9FE', icon: '🎬' };
      case 'BEST_CASE_SCRIPT':
        return { label: 'Best-Case Script', color: '#92400E', bg: '#FEF3C7', icon: '📄' };
      case 'REALTIME_ASSIST':
        return { label: 'Realtime Assist', color: '#854D0E', bg: '#FEF9C3', icon: '💡' };
      case 'CALIBRATION_ANALYSIS':
        return { label: 'Rubric Calibration', color: '#9D174D', bg: '#FCE7F3', icon: '🎯' };
      default:
        return { label: String(purpose).replace(/_/g, ' '), color: '#374151', bg: '#F3F4F6', icon: '🤖' };
    }
  };

  const handleCopy = (text: string, type: 'prompt' | 'response') => {
    navigator.clipboard.writeText(text);
    if (type === 'prompt') {
      setCopiedPrompt(true);
      setTimeout(() => setCopiedPrompt(false), 2000);
    } else {
      setCopiedResponse(true);
      setTimeout(() => setCopiedResponse(false), 2000);
    }
  };

  return (
    <div className="admin-page">
      {/* Standardized Admin Header & Banner */}
      <AdminPageHeader
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'LLM Execution & Token Audit Trail' },
        ]}
        icon="🤖"
        title="LLM Execution & Token Audit Trail"
        badge="Observability"
        description="Audit all LLM prompt invocations, input/output token consumptions, model latencies, costs, and raw request-response payloads across simulation dialogue turns, evaluation scoring, scenario generation, and realtime assist."
        actions={
          isPlatformAdmin ? (
            <div ref={tenantDropdownRef} style={{ minWidth: '260px', position: 'relative' }}>
              <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 600, color: '#93C5FD', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Filter Organization
              </label>
              <button
                type="button"
                onClick={() => setIsTenantDropdownOpen(!isTenantDropdownOpen)}
                style={{
                  width: '100%',
                  padding: '9px 14px',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  color: '#FFFFFF',
                  fontSize: '13px',
                  fontWeight: 600,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {activeTenantName}
                </span>
                <span style={{ fontSize: '10px', opacity: 0.8 }}>▼</span>
              </button>

              {isTenantDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '6px',
                    width: '320px',
                    background: '#FFFFFF',
                    borderRadius: '10px',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
                    border: '1px solid #E2E8F0',
                    zIndex: 100,
                    padding: '8px',
                    color: '#0F172A',
                  }}
                >
                  <input
                    type="text"
                    placeholder="Search organization…"
                    value={tenantSearchText}
                    onChange={(e) => setTenantSearchText(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '7px 10px',
                      borderRadius: '6px',
                      border: '1px solid #CBD5E1',
                      fontSize: '12.5px',
                      marginBottom: '6px',
                    }}
                    autoFocus
                  />
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    <div
                      onClick={() => {
                        setSelectedTenantId('all');
                        setCurrentPage(1);
                        setIsTenantDropdownOpen(false);
                      }}
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12.5px',
                        background: selectedTenantId === 'all' ? '#EFF6FF' : 'transparent',
                        fontWeight: selectedTenantId === 'all' ? 700 : 400,
                        color: selectedTenantId === 'all' ? '#1D4ED8' : '#334155',
                      }}
                    >
                      🏢 All Client Organizations (Global)
                    </div>
                    {filteredTenantsList.map((t) => (
                      <div
                        key={t.id}
                        onClick={() => {
                          setSelectedTenantId(t.id);
                          setCurrentPage(1);
                          setIsTenantDropdownOpen(false);
                        }}
                        style={{
                          padding: '8px 10px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12.5px',
                          background: selectedTenantId === t.id ? '#EFF6FF' : 'transparent',
                          fontWeight: selectedTenantId === t.id ? 700 : 400,
                          color: selectedTenantId === t.id ? '#1D4ED8' : '#334155',
                        }}
                      >
                        {t.name} <span style={{ fontSize: '11px', color: '#94A3B8' }}>({t.slug})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : undefined
        }
      />

      {/* Date filter — primary control, kept above the summary metrics */}
      <div className="llm-date-filter-bar">
        <div className="llm-date-filter-bar__left">
          <span className="llm-date-filter-bar__label">
            <span>📅</span> Date Range:
          </span>
          {([
            { id: 'all', label: 'All Time' },
            { id: 'today', label: 'Today' },
            { id: '7d', label: 'Last 7 Days' },
            { id: '30d', label: 'Last 30 Days' },
          ] as const).map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`llm-date-preset-btn ${datePreset === preset.id ? 'active' : ''}`}
              onClick={() => handleDatePreset(preset.id)}
            >
              {preset.label}
            </button>
          ))}
        </div>

        <div className="llm-date-filter-bar__right">
          <div className="llm-date-input-wrap">
            <label htmlFor="llm-start-date">From</label>
            <input
              id="llm-start-date"
              type="date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
            />
          </div>
          <div className="llm-date-input-wrap">
            <label htmlFor="llm-end-date">To</label>
            <input
              id="llm-end-date"
              type="date"
              value={endDate}
              onChange={(e) => handleEndDateChange(e.target.value)}
            />
          </div>
          {(startDate || endDate) && (
            <button
              type="button"
              className="llm-date-clear-btn"
              onClick={() => handleDatePreset('all')}
              title="Reset date filter to All Time"
            >
              ✕ Clear Date
            </button>
          )}
          <span className="llm-date-filter-summary" aria-live="polite">
            {isLoading ? '⏳ Loading…' : `📊 ${totalCount.toLocaleString()} log${totalCount === 1 ? '' : 's'}`}
            <strong>{dateFilterLabel}</strong>
          </span>
        </div>
      </div>

      {fetchError && (
        <div className="llm-audit-error" role="alert">
          <span>⚠️ {fetchError}</span>
          <button type="button" onClick={fetchAuditLogs}>Retry</button>
        </div>
      )}

      {/* KPI Overview Grid */}
      <div className="llm-stat-grid">
        {/* Card 1: Total LLM Invocations */}
        <div className="llm-stat-card">
          <div className="llm-stat-card__header">
            <span className="llm-stat-card__title">Total LLM Invocations</span>
            <span className="llm-stat-card__icon" style={{ background: 'var(--blue-soft, #E8F0FE)', color: 'var(--blue, #0D4FBD)' }}>
              🤖
            </span>
          </div>
          <div className="llm-stat-card__value">
            {summary ? summary.totalCalls.toLocaleString() : (isLoading ? '…' : '0')}
          </div>
          <div className="llm-stat-card__sub">
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--ok, #0F8A5F)',
                background: 'var(--ok-soft, #E4F5EE)',
                padding: '3px 8px',
                borderRadius: '6px',
                border: '1px solid rgba(15, 138, 95, 0.15)',
              }}
            >
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--ok, #0F8A5F)' }} />
              {summary && summary.totalCalls > 0
                ? `${Math.round((summary.successfulCalls / summary.totalCalls) * 100)}% Success Rate`
                : '100% Operational'}
            </span>
          </div>
        </div>

        {/* Card 2: Total Tokens Consumed */}
        <div className="llm-stat-card">
          <div className="llm-stat-card__header">
            <span className="llm-stat-card__title">Total Tokens Consumed</span>
            <span className="llm-stat-card__icon" style={{ background: '#FEF3C7', color: '#92400E' }}>
              🪙
            </span>
          </div>
          <div className="llm-stat-card__value">
            {summary ? summary.totalTokens.toLocaleString() : (isLoading ? '…' : '0')}
          </div>
          <div className="llm-stat-card__sub" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span>
              Prompt: <strong style={{ color: 'var(--ink, #0E1B33)' }}>{summary ? summary.totalPromptTokens.toLocaleString() : 0}</strong>
            </span>
            <span style={{ color: 'var(--faint, #8A94A8)' }}>•</span>
            <span>
              Comp: <strong style={{ color: 'var(--ink, #0E1B33)' }}>{summary ? summary.totalCompletionTokens.toLocaleString() : 0}</strong>
            </span>
          </div>
        </div>

        {/* Card 3: Total Estimated Cost */}
        <div className="llm-stat-card">
          <div className="llm-stat-card__header">
            <span className="llm-stat-card__title">Total Estimated Cost</span>
            <span className="llm-stat-card__icon" style={{ background: 'var(--ok-soft, #E4F5EE)', color: 'var(--ok, #0F8A5F)' }}>
              💰
            </span>
          </div>
          <div className="llm-stat-card__value" style={{ color: 'var(--ok, #0F8A5F)' }}>
            ${summary ? summary.totalEstimatedCost.toFixed(4) : (isLoading ? '…' : '0.0000')}
          </div>
          <div className="llm-stat-card__sub">
            Calculated via model-specific token tariffs
          </div>
        </div>

        {/* Card 4: Average Response Latency */}
        <div className="llm-stat-card">
          <div className="llm-stat-card__header">
            <span className="llm-stat-card__title">Average Response Latency</span>
            <span className="llm-stat-card__icon" style={{ background: '#EEF2FF', color: '#4F46E5' }}>
              ⚡
            </span>
          </div>
          <div className="llm-stat-card__value">
            {summary ? `${summary.avgLatencyMs.toLocaleString()} ms` : (isLoading ? '…' : '0 ms')}
          </div>
          <div className="llm-stat-card__sub">
            Average turn-around per prompt response
          </div>
        </div>
      </div>

      {/* Economical Consumption & Telemetry Center */}
      <div
        className="adm-card"
        style={{
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          background: '#FFFFFF',
          border: '1px solid var(--line, #E2E8F0)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '20px' }}>📊</span>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--navy)' }}>
                Economical Consumption &amp; Cost Telemetry
              </h3>
              <span style={{ fontSize: '11px', background: '#DCFCE7', color: '#15803D', padding: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>
                FinOps Intelligence
              </span>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: '12.5px', color: 'var(--muted)' }}>
              Live breakdown of API token usage, monetary cost, and prompt volume by simulation scenario, tenant organization, and learner attempt.
            </p>
          </div>

          {/* Breakdown Tabs */}
          <div style={{ display: 'flex', gap: '6px', background: '#F1F5F9', padding: '4px', borderRadius: '8px' }}>
            <button
              type="button"
              onClick={() => setActiveBreakdownTab('simulations')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                background: activeBreakdownTab === 'simulations' ? '#FFFFFF' : 'transparent',
                color: activeBreakdownTab === 'simulations' ? 'var(--blue, #1D4ED8)' : '#64748B',
                fontWeight: activeBreakdownTab === 'simulations' ? 700 : 500,
                fontSize: '12px',
                cursor: 'pointer',
                boxShadow: activeBreakdownTab === 'simulations' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              🎯 Simulations ({Object.keys(summary?.simulationBreakdown || {}).length})
            </button>
            <button
              type="button"
              onClick={() => setActiveBreakdownTab('tenants')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                background: activeBreakdownTab === 'tenants' ? '#FFFFFF' : 'transparent',
                color: activeBreakdownTab === 'tenants' ? 'var(--blue, #1D4ED8)' : '#64748B',
                fontWeight: activeBreakdownTab === 'tenants' ? 700 : 500,
                fontSize: '12px',
                cursor: 'pointer',
                boxShadow: activeBreakdownTab === 'tenants' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              🏢 Organizations ({Object.keys(summary?.tenantBreakdown || {}).length})
            </button>
            <button
              type="button"
              onClick={() => setActiveBreakdownTab('attempts')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                background: activeBreakdownTab === 'attempts' ? '#FFFFFF' : 'transparent',
                color: activeBreakdownTab === 'attempts' ? 'var(--blue, #1D4ED8)' : '#64748B',
                fontWeight: activeBreakdownTab === 'attempts' ? 700 : 500,
                fontSize: '12px',
                cursor: 'pointer',
                boxShadow: activeBreakdownTab === 'attempts' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              📈 Attempts ({Object.keys(summary?.attemptBreakdown || {}).length})
            </button>
          </div>
        </div>

        {/* Tab 1: Simulation-Wise Consumption */}
        {activeBreakdownTab === 'simulations' && (
          <div style={{ overflowX: 'auto' }}>
            <table className="ad-RP" style={{ width: '100%', fontSize: '12.5px' }}>
              <thead>
                <tr>
                  <th>Simulation Scenario</th>
                  <th className="ad-c" style={{ width: '120px' }}>Invocations</th>
                  <th className="ad-c" style={{ width: '130px' }}>Tokens Burned</th>
                  <th className="ad-c" style={{ width: '110px' }}>Total Cost</th>
                  <th className="ad-c" style={{ width: '120px' }}>Avg Cost/Call</th>
                  <th style={{ width: '110px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {!summary?.simulationBreakdown || Object.keys(summary.simulationBreakdown).length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: 'var(--muted)' }}>
                      No simulation telemetry recorded yet.
                    </td>
                  </tr>
                ) : (
                  Object.values(summary.simulationBreakdown).map((sim) => {
                    const isFiltered = simulationFilter === sim.scenarioId;
                    const avgCost = sim.count > 0 ? (sim.cost / sim.count) : 0;
                    const sharePct = summary && summary.totalCalls > 0 ? Math.round((sim.count / summary.totalCalls) * 100) : 0;
                    return (
                      <tr key={sim.scenarioId} style={{ background: isFiltered ? '#EFF6FF' : undefined }}>
                        <td>
                          <div style={{ fontWeight: 700, color: 'var(--navy)' }}>{sim.scenarioTitle}</div>
                          <span style={{ fontSize: '11px', color: 'var(--muted)', fontFamily: 'monospace' }}>{sim.scenarioId}</span>
                        </td>
                        <td className="ad-c">
                          <strong style={{ color: 'var(--navy)' }}>{sim.count}</strong>
                          <span style={{ fontSize: '11px', color: 'var(--muted)', marginLeft: '4px' }}>({sharePct}%)</span>
                        </td>
                        <td className="ad-c" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {Number(sim.tokens || 0).toLocaleString()}
                        </td>
                        <td className="ad-c" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#0F8A5F' }}>
                          ${Number(sim.cost || 0).toFixed(4)}
                        </td>
                        <td className="ad-c" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--muted)' }}>
                          ${Number(avgCost || 0).toFixed(5)}
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => {
                              setSimulationFilter(isFiltered ? 'all' : sim.scenarioId);
                              setCurrentPage(1);
                            }}
                            style={{
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '11.5px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              border: isFiltered ? '1px solid #3B82F6' : '1px solid #CBD5E1',
                              background: isFiltered ? '#1D4ED8' : '#FFFFFF',
                              color: isFiltered ? '#FFFFFF' : '#1E293B',
                            }}
                          >
                            {isFiltered ? '✕ Clear' : '🔍 Filter'}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Tab 2: Tenant-Wise Consumption */}
        {activeBreakdownTab === 'tenants' && (
          <div style={{ overflowX: 'auto' }}>
            <table className="ad-RP" style={{ width: '100%', fontSize: '12.5px' }}>
              <thead>
                <tr>
                  <th>Client Organization</th>
                  <th className="ad-c" style={{ width: '120px' }}>Invocations</th>
                  <th className="ad-c" style={{ width: '130px' }}>Tokens Burned</th>
                  <th className="ad-c" style={{ width: '110px' }}>Total Cost</th>
                  <th className="ad-c" style={{ width: '120px' }}>Avg Cost/Call</th>
                  <th style={{ width: '110px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {!summary?.tenantBreakdown || Object.keys(summary.tenantBreakdown).length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: 'var(--muted)' }}>
                      No organization telemetry recorded yet.
                    </td>
                  </tr>
                ) : (
                  Object.values(summary.tenantBreakdown).map((t) => {
                    const isFiltered = selectedTenantId === t.tenantId;
                    const avgCost = t.count > 0 ? (t.cost / t.count) : 0;
                    const sharePct = summary && summary.totalCalls > 0 ? Math.round((t.count / summary.totalCalls) * 100) : 0;
                    return (
                      <tr key={t.tenantId} style={{ background: isFiltered ? '#EFF6FF' : undefined }}>
                        <td>
                          <div style={{ fontWeight: 700, color: 'var(--navy)' }}>{t.tenantName}</div>
                          <span style={{ fontSize: '11px', color: 'var(--muted)', fontFamily: 'monospace' }}>{t.tenantId}</span>
                        </td>
                        <td className="ad-c">
                          <strong style={{ color: 'var(--navy)' }}>{t.count}</strong>
                          <span style={{ fontSize: '11px', color: 'var(--muted)', marginLeft: '4px' }}>({sharePct}%)</span>
                        </td>
                        <td className="ad-c" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {Number(t.tokens || 0).toLocaleString()}
                        </td>
                        <td className="ad-c" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#0F8A5F' }}>
                          ${Number(t.cost || 0).toFixed(4)}
                        </td>
                        <td className="ad-c" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--muted)' }}>
                          ${Number(avgCost || 0).toFixed(5)}
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedTenantId(isFiltered ? 'all' : t.tenantId);
                              setCurrentPage(1);
                            }}
                            style={{
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '11.5px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              border: isFiltered ? '1px solid #3B82F6' : '1px solid #CBD5E1',
                              background: isFiltered ? '#1D4ED8' : '#FFFFFF',
                              color: isFiltered ? '#FFFFFF' : '#1E293B',
                            }}
                          >
                            {isFiltered ? '✕ Clear' : '🔍 Filter'}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Tab 3: Attempt-Wise Consumption */}
        {activeBreakdownTab === 'attempts' && (
          <div style={{ overflowX: 'auto' }}>
            <table className="ad-RP" style={{ width: '100%', fontSize: '12.5px' }}>
              <thead>
                <tr>
                  <th>Attempt Reference</th>
                  <th>Scenario</th>
                  <th className="ad-c" style={{ width: '110px' }}>Invocations</th>
                  <th className="ad-c" style={{ width: '120px' }}>Tokens Burned</th>
                  <th className="ad-c" style={{ width: '110px' }}>Attempt Cost</th>
                  <th style={{ width: '110px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {!summary?.attemptBreakdown || Object.keys(summary.attemptBreakdown).length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: 'var(--muted)' }}>
                      No attempt-specific executions recorded yet.
                    </td>
                  </tr>
                ) : (
                  Object.values(summary.attemptBreakdown).map((att) => {
                    const isFiltered = attemptFilter.trim() === att.attemptId;
                    return (
                      <tr key={att.attemptId} style={{ background: isFiltered ? '#EFF6FF' : undefined }}>
                        <td>
                          <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1D4ED8', background: '#DBEAFE', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                            {att.attemptId}
                          </span>
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{att.scenarioTitle}</div>
                        </td>
                        <td className="ad-c">
                          <strong style={{ color: 'var(--navy)' }}>{att.count}</strong>
                        </td>
                        <td className="ad-c" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {Number(att.tokens || 0).toLocaleString()}
                        </td>
                        <td className="ad-c" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#0F8A5F' }}>
                          ${Number(att.cost || 0).toFixed(4)}
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => {
                              setAttemptFilter(isFiltered ? '' : att.attemptId);
                              setCurrentPage(1);
                            }}
                            style={{
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '11.5px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              border: isFiltered ? '1px solid #3B82F6' : '1px solid #CBD5E1',
                              background: isFiltered ? '#1D4ED8' : '#FFFFFF',
                              color: isFiltered ? '#FFFFFF' : '#1E293B',
                            }}
                          >
                            {isFiltered ? '✕ Clear' : '🔍 Filter'}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Purpose Filter Strip */}
      <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '12px', marginBottom: '16px' }}>
        {[
          { id: 'all', label: 'All Executions', icon: '🌐' },
          { id: 'SIMULATION_DIALOGUE', label: 'Dialogue Turns', icon: '💬' },
          { id: 'SIMULATION_EVALUATION', label: 'Scoring & Rubrics', icon: '⚖️' },
          { id: 'SCENARIO_CREATION', label: 'Scenario Generation', icon: '🎬' },
          { id: 'BEST_CASE_SCRIPT', label: 'Best-Case Script', icon: '📄' },
          { id: 'REALTIME_ASSIST', label: 'Realtime Assist', icon: '💡' },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              setPurposeFilter(tab.id);
              setCurrentPage(1);
            }}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: purposeFilter === tab.id ? '2px solid var(--blue, #3B5BDB)' : '1px solid var(--line, #E2E8F0)',
              background: purposeFilter === tab.id ? 'var(--blue-soft, #EFF6FF)' : 'var(--white, #FFFFFF)',
              color: purposeFilter === tab.id ? 'var(--blue, #1D4ED8)' : 'var(--ink, #334155)',
              fontWeight: purposeFilter === tab.id ? 700 : 500,
              fontSize: '12.5px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s ease',
            }}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
            {summary?.purposeBreakdown?.[tab.id] && (
              <span
                style={{
                  background: purposeFilter === tab.id ? '#BFDBFE' : '#F1F5F9',
                  padding: '1px 6px',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: 700,
                }}
              >
                {summary.purposeBreakdown[tab.id].count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filter & Search Bar */}
      <div
        style={{
          background: '#FFFFFF',
          border: '1px solid var(--line, #E2E8F0)',
          borderRadius: '12px',
          padding: '14px 18px',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '280px' }}>
          <div className="ad-srch" style={{ flex: 1, height: '36px' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              placeholder="Search prompt, response, model, learner, attempt ID…"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* User / Learner Filter */}
          <select
            value={actorFilter}
            onChange={(e) => {
              setActorFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              padding: '7px 12px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              fontSize: '12.5px',
              color: '#334155',
              background: '#FFFFFF',
              maxWidth: '190px',
            }}
          >
            <option value="all">👤 All Learners / Users</option>
            {availableActors.map((actor) => (
              <option key={actor.id} value={actor.id}>
                {actor.name} ({actor.count})
              </option>
            ))}
          </select>

          {/* Simulation Filter */}
          <select
            value={simulationFilter}
            onChange={(e) => {
              setSimulationFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              padding: '7px 12px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              fontSize: '12.5px',
              color: '#334155',
              background: '#FFFFFF',
              maxWidth: '190px',
            }}
          >
            <option value="all">All Simulations</option>
            {availableSimulations.map((sim) => (
              <option key={sim.id} value={sim.id}>
                {sim.title}
              </option>
            ))}
          </select>

          {/* Attempt ID Filter */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <input
              placeholder="Filter Attempt ID…"
              value={attemptFilter}
              onChange={(e) => {
                setAttemptFilter(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: '7px 24px 7px 10px',
                borderRadius: '8px',
                border: '1px solid #CBD5E1',
                fontSize: '12px',
                fontFamily: 'monospace',
                width: '140px',
              }}
            />
            {attemptFilter && (
              <button
                type="button"
                onClick={() => {
                  setAttemptFilter('');
                  setCurrentPage(1);
                }}
                style={{
                  position: 'absolute',
                  right: '6px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '11px',
                  color: '#94A3B8',
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Model Filter */}
          <select
            value={modelFilter}
            onChange={(e) => {
              setModelFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              padding: '7px 12px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              fontSize: '12.5px',
              color: '#334155',
              background: '#FFFFFF',
            }}
          >
            <option value="all">All Models</option>
            <option value="gpt-4o">GPT-4o</option>
            <option value="gpt-4o-mini">GPT-4o-Mini</option>
            <option value="claude-3-5-sonnet">Claude 3.5 Sonnet</option>
            <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
            <option value="llama3:8b">Llama 3 (8B)</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              padding: '7px 12px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              fontSize: '12.5px',
              color: '#334155',
              background: '#FFFFFF',
            }}
          >
            <option value="all">All Statuses</option>
            <option value="SUCCESS">Success</option>
            <option value="FAILED">Failed</option>
          </select>

          <button
            type="button"
            onClick={fetchAuditLogs}
            style={{
              padding: '7px 14px',
              borderRadius: '8px',
              border: '1px solid #CBD5E1',
              background: '#F8FAFC',
              color: '#334155',
              fontSize: '12.5px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span>🔄</span> Refresh
          </button>
        </div>
      </div>

      {/* LLM Audit Logs Table */}
      <div className="adm-card" style={{ borderRadius: '12px', overflow: 'hidden' }}>
        <div className="llm-audit-results-bar">
          <div className="llm-audit-results-bar__meta">
            <strong>Audit Entries</strong>
            <span className="llm-audit-results-bar__chip">📅 {dateFilterLabel}</span>
            <span className="llm-audit-results-bar__count">
              {isLoading ? 'Loading…' : `${totalCount.toLocaleString()} matching log${totalCount === 1 ? '' : 's'}`}
            </span>
          </div>
          {(startDate || endDate) && (
            <button type="button" className="llm-date-clear-btn" onClick={() => handleDatePreset('all')}>
              ✕ Clear date filter
            </button>
          )}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="ad-RP" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: '130px' }}>Timestamp</th>
                <th style={{ width: '150px' }}>Purpose</th>
                <th style={{ width: '180px' }}>User / Learner</th>
                <th style={{ width: '130px' }}>Model &amp; Provider</th>
                <th>Scenario / Attempt Reference</th>
                <th>Prompt &amp; Response Snippet</th>
                <th className="ad-c" style={{ width: '100px' }}>Tokens</th>
                <th className="ad-c" style={{ width: '85px' }}>Latency</th>
                <th className="ad-c" style={{ width: '80px' }}>Cost</th>
                <th className="ad-c" style={{ width: '80px' }}>Status</th>
                <th style={{ width: '85px' }}></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)' }}>
                    <div className="dashboard-spinner" style={{ margin: '0 auto 12px' }} />
                    Loading LLM execution audit records &amp; token telemetry…
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', padding: '36px', color: 'var(--muted)' }}>
                    No LLM prompt executions match the selected filters
                    {startDate || endDate ? ` for ${dateFilterLabel}` : ''}.
                  </td>
                </tr>
              ) : (
                logs.map((log) => {
                  const badge = getPurposeBadge(log.purpose);
                  const parsedDate = log.createdAt ? new Date(log.createdAt) : null;
                  const isValidDate = parsedDate && !isNaN(parsedDate.getTime());
                  const formattedTime = isValidDate
                    ? parsedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                    : '—';
                  const formattedDate = isValidDate
                    ? parsedDate.toLocaleDateString([], { day: 'numeric', month: 'short' })
                    : '—';

                  return (
                    <tr key={log.id}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '12px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{formattedTime}</div>
                        <span style={{ fontSize: '11px', color: 'var(--muted)' }}>{formattedDate}</span>
                      </td>

                      <td>
                        <span
                          style={{
                            background: badge.bg,
                            color: badge.color,
                            padding: '3px 8px',
                            borderRadius: '6px',
                            fontSize: '11.5px',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <span>{badge.icon}</span>
                          <span>{badge.label}</span>
                        </span>
                      </td>

                      <td>
                        <div className="llm-actor-cell">
                          <div className="llm-actor-avatar" title={log.actorName || log.actorEmail || 'Learner'}>
                            {getActorInitials(log.actorName, log.actorEmail)}
                          </div>
                          <div className="llm-actor-info">
                            <span className="llm-actor-name" title={log.actorName || log.actorEmail || 'Learner'}>
                              {log.actorName || log.actorEmail || 'Learner'}
                            </span>
                            <span className="llm-actor-email" title={log.actorEmail || log.actorId || ''}>
                              {log.actorEmail || log.actorId || '—'}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                              <span className={`llm-actor-role-badge ${String(log.actorRole || 'learner').toLowerCase()}`}>
                                {log.actorRole || 'LEARNER'}
                              </span>
                              {log.actorRole && log.actorRole !== 'LEARNER' && (
                                <span style={{ fontSize: '9.5px', color: 'var(--muted)', background: '#F1F5F9', padding: '1px 4px', borderRadius: '4px' }}>
                                  Sim: Learner
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div style={{ fontWeight: 700, fontSize: '12.5px', color: 'var(--navy)' }}>
                          {log.model}
                        </div>
                        <span style={{ fontSize: '11px', color: 'var(--muted)', textTransform: 'uppercase' }}>
                          {log.provider}
                        </span>
                      </td>

                      <td>
                        <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--navy)', maxWidth: '220px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {log.scenarioTitle || log.scenarioId || 'General Blueprint'}
                        </div>
                        {log.attemptId && (
                          <div style={{ fontSize: '11px', color: 'var(--blue)', fontFamily: 'monospace' }}>
                            Attempt: {log.attemptId} {log.turnNumber ? `(Turn ${log.turnNumber})` : ''}
                          </div>
                        )}
                      </td>

                      <td>
                        <div style={{ maxWidth: '260px', fontSize: '12px', color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <strong style={{ color: 'var(--navy)' }}>Prompt: </strong>
                          {getPromptSnippet(log.prompt)}
                        </div>
                        <div style={{ maxWidth: '260px', fontSize: '12px', color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <strong style={{ color: '#0F8A5F' }}>Response: </strong>
                          {getResponseSnippet(log.response)}
                        </div>
                      </td>

                      <td className="ad-c" style={{ fontVariantNumeric: 'tabular-nums', fontSize: '12.5px' }}>
                        <div style={{ fontWeight: 700, color: 'var(--navy)' }}>
                          {log.totalTokens ?? (Number(log.promptTokens || 0) + Number(log.completionTokens || 0))}
                        </div>
                        <span style={{ fontSize: '10.5px', color: 'var(--muted)' }}>
                          {log.promptTokens ?? 0} in / {log.completionTokens ?? 0} out
                        </span>
                      </td>

                      <td className="ad-c" style={{ fontVariantNumeric: 'tabular-nums', fontSize: '12px', color: 'var(--ink)' }}>
                        {log.latencyMs ?? 0} ms
                      </td>

                      <td className="ad-c" style={{ fontVariantNumeric: 'tabular-nums', fontSize: '12px', fontWeight: 600, color: '#0F8A5F' }}>
                        ${Number(log.estimatedCost || 0).toFixed(4)}
                      </td>

                      <td className="ad-c">
                        <div style={{ display: 'inline-flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                          <span
                            className={`ad-res ${log.status === 'SUCCESS' ? 'ad-res-pass' : 'ad-res-fail'}`}
                            style={{ fontSize: '10.5px', padding: '2px 7px' }}
                            title="Pipeline LLM Execution Status"
                          >
                            <i />
                            API {log.status}
                          </span>
                          {(log.learnerOutcome || (log.purpose === 'SIMULATION_EVALUATION' && log.response)) && (
                            <span
                              style={{
                                fontSize: '10px',
                                fontWeight: 700,
                                padding: '1px 6px',
                                borderRadius: '4px',
                                background: (log.learnerOutcome === 'PASSED' || (log.response && log.response.includes('"isPassed":true'))) ? '#DCFCE7' : '#FEE2E2',
                                color: (log.learnerOutcome === 'PASSED' || (log.response && log.response.includes('"isPassed":true'))) ? '#15803D' : '#B91C1C',
                              }}
                              title="Learner Simulation Performance Outcome"
                            >
                              {(log.learnerOutcome === 'PASSED' || (log.response && log.response.includes('"isPassed":true'))) ? '✓ Learner Pass' : '⚠️ Needs Work'}
                            </span>
                          )}
                        </div>
                      </td>

                      <td>
                        <button
                          type="button"
                          onClick={() => setSelectedLog(log)}
                          style={{
                            background: '#EFF6FF',
                            border: '1px solid #BFDBFE',
                            color: '#1D4ED8',
                            fontSize: '11.5px',
                            fontWeight: 700,
                            padding: '4px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Inspect 🔍
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Enterprise Data Table Pagination Footer */}
        <div className="dt-pagination-bar">
          <div className="dt-pagination-bar__left">
            <div className="dt-records-info">
              {totalCount === 0 ? (
                'Showing 0 records'
              ) : (
                <>
                  Showing <strong>{((currentPage - 1) * pageSize + 1).toLocaleString()}</strong> to{' '}
                  <strong>{Math.min(currentPage * pageSize, totalCount).toLocaleString()}</strong> of{' '}
                  <strong>{totalCount.toLocaleString()}</strong> records
                </>
              )}
            </div>

            <div className="dt-page-size-selector">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>per page</span>
            </div>
          </div>

          <div className="dt-pagination-bar__right">
            <button
              type="button"
              className="dt-btn"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1 || isLoading}
              title="First Page"
            >
              « First
            </button>
            <button
              type="button"
              className="dt-btn"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1 || isLoading}
              title="Previous Page"
            >
              ‹ Prev
            </button>

            {paginationPages.map((page, idx) => {
              if (page === '...') {
                return (
                  <span key={`ellipsis-${idx}`} className="dt-ellipsis">
                    …
                  </span>
                );
              }
              const pageNum = Number(page);
              return (
                <button
                  key={pageNum}
                  type="button"
                  className={`dt-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                  disabled={isLoading}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              type="button"
              className="dt-btn"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || isLoading}
              title="Next Page"
            >
              Next ›
            </button>
            <button
              type="button"
              className="dt-btn"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || isLoading}
              title="Last Page"
            >
              Last »
            </button>

            {totalPages > 5 && (
              <form
                className="dt-jump-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  const target = parseInt(jumpPage, 10);
                  if (!isNaN(target) && target >= 1 && target <= totalPages) {
                    setCurrentPage(target);
                    setJumpPage('');
                  }
                }}
              >
                <span>Go to</span>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={jumpPage}
                  onChange={(e) => setJumpPage(e.target.value)}
                  placeholder={String(currentPage)}
                  className="dt-jump-input"
                />
                <button type="submit" className="dt-jump-btn">
                  Go
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced LLM Execution Audit Inspector Modal */}
      {selectedLog && (
        <InspectorModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
          onCopy={handleCopy}
          copiedPrompt={copiedPrompt}
          copiedResponse={copiedResponse}
          getPurposeBadge={getPurposeBadge}
        />
      )}
    </div>
  );
};

interface InspectorModalProps {
  log: LLMAuditRecord;
  onClose: () => void;
  onCopy: (text: string, type: 'prompt' | 'response') => void;
  copiedPrompt: boolean;
  copiedResponse: boolean;
  getPurposeBadge: (purpose: string) => { label: string; color: string; bg: string; icon: string };
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. SYNTAX CODE VIEWER (Line-numbered, highlighted, searchable, copyable)
// ─────────────────────────────────────────────────────────────────────────────
interface SyntaxCodeViewerProps {
  code: string;
  title?: string;
  defaultWrap?: boolean;
  isJson?: boolean;
  accentColor?: string;
  onCopy?: () => void;
  copied?: boolean;
}

const SyntaxCodeViewer: React.FC<SyntaxCodeViewerProps> = ({
  code,
  title,
  defaultWrap = true,
  isJson,
  accentColor = '#38BDF8',
  onCopy,
  copied,
}) => {
  const [wrap, setWrap] = useState(defaultWrap);
  const [search, setSearch] = useState('');
  const [internalCopied, setInternalCopied] = useState(false);

  // Format code if JSON
  const formattedCode = useMemo(() => {
    if (!code) return '';
    if (isJson || code.trim().startsWith('{') || code.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(code.trim());
        return JSON.stringify(parsed, null, 2);
      } catch {
        return code;
      }
    }
    return code;
  }, [code, isJson]);

  const lines = useMemo(() => {
    return formattedCode.split('\n');
  }, [formattedCode]);

  const handleCopy = () => {
    if (onCopy) {
      onCopy();
    } else {
      navigator.clipboard.writeText(formattedCode);
      setInternalCopied(true);
      setTimeout(() => setInternalCopied(false), 2000);
    }
  };

  const isCopied = copied !== undefined ? copied : internalCopied;

  // Filter matching line indices
  const matchingLineCount = useMemo(() => {
    if (!search.trim()) return 0;
    const term = search.toLowerCase();
    return lines.filter((l) => l.toLowerCase().includes(term)).length;
  }, [lines, search]);

  // Syntax colorizer for JSON tokens
  const renderTokenizedLine = (line: string) => {
    if (!line) return <span>&nbsp;</span>;

    // Highlight search match if search active
    if (search.trim()) {
      const term = search.toLowerCase();
      const idx = line.toLowerCase().indexOf(term);
      if (idx !== -1) {
        const before = line.slice(0, idx);
        const match = line.slice(idx, idx + term.length);
        const after = line.slice(idx + term.length);
        return (
          <span>
            {renderTokens(before)}
            <mark style={{ background: '#FDE047', color: '#0F172A', borderRadius: '2px', padding: '0 2px' }}>
              {match}
            </mark>
            {renderTokenizedLine(after)}
          </span>
        );
      }
    }

    return renderTokens(line);
  };

  const renderTokens = (text: string) => {
    // Basic regex-based token highlighting for JSON / code
    const parts: React.ReactNode[] = [];
    const jsonRegex = /("(?:\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = jsonRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(<span key={lastIndex} style={{ color: '#94A3B8' }}>{text.slice(lastIndex, match.index)}</span>);
      }

      const val = match[0];
      const isKey = match[2] !== undefined;

      if (isKey) {
        parts.push(
          <span key={match.index} style={{ color: '#38BDF8', fontWeight: 600 }}>
            {val.replace(/:$/, '')}
          </span>,
          <span key={`${match.index}-colon`} style={{ color: '#E2E8F0' }}>:</span>
        );
      } else if (val.startsWith('"')) {
        parts.push(
          <span key={match.index} style={{ color: '#86EFAC' }}>
            {val}
          </span>
        );
      } else if (val === 'true' || val === 'false') {
        parts.push(
          <span key={match.index} style={{ color: '#F472B6', fontWeight: 600 }}>
            {val}
          </span>
        );
      } else if (val === 'null') {
        parts.push(
          <span key={match.index} style={{ color: '#C084FC', fontStyle: 'italic' }}>
            {val}
          </span>
        );
      } else {
        parts.push(
          <span key={match.index} style={{ color: '#FDE047' }}>
            {val}
          </span>
        );
      }

      lastIndex = jsonRegex.lastIndex;
    }

    if (lastIndex < text.length) {
      parts.push(<span key={lastIndex} style={{ color: '#E2E8F0' }}>{text.slice(lastIndex)}</span>);
    }

    return parts.length > 0 ? parts : <span style={{ color: '#E2E8F0' }}>{text}</span>;
  };

  return (
    <div
      style={{
        background: '#0B132B',
        border: '1px solid #1E293B',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
      }}
    >
      {/* Viewer Toolbar */}
      <div
        style={{
          background: '#0F172A',
          borderBottom: '1px solid #1E293B',
          padding: '8px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', fontWeight: 700, color: accentColor, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {title || (isJson ? 'JSON Payload' : 'Text Payload')}
          </span>
          <span style={{ fontSize: '11px', color: '#64748B', background: '#1E293B', padding: '2px 8px', borderRadius: '4px' }}>
            {lines.length} lines • {formattedCode.length.toLocaleString()} chars
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Quick Search */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Find in payload…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                background: '#1E293B',
                border: '1px solid #334155',
                color: '#F8FAFC',
                padding: '4px 8px',
                borderRadius: '5px',
                fontSize: '11px',
                width: '130px',
                outline: 'none',
              }}
            />
            {search.trim() && (
              <span style={{ marginLeft: '4px', fontSize: '10.5px', color: matchingLineCount > 0 ? '#38BDF8' : '#F87171' }}>
                {matchingLineCount} found
              </span>
            )}
          </div>

          {/* Wrap Toggle */}
          <button
            type="button"
            onClick={() => setWrap(!wrap)}
            style={{
              background: wrap ? '#1E3A8A' : '#1E293B',
              color: wrap ? '#BFDBFE' : '#94A3B8',
              border: '1px solid #334155',
              borderRadius: '5px',
              padding: '4px 10px',
              fontSize: '11px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {wrap ? 'Wrap: ON' : 'Wrap: OFF'}
          </button>

          {/* Copy Button */}
          <button
            type="button"
            onClick={handleCopy}
            style={{
              background: isCopied ? '#065F46' : '#1E293B',
              color: isCopied ? '#6EE7B7' : '#F1F5F9',
              border: isCopied ? '1px solid #10B981' : '1px solid #334155',
              borderRadius: '5px',
              padding: '4px 12px',
              fontSize: '11px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {isCopied ? '✓ Copied' : '📋 Copy'}
          </button>
        </div>
      </div>

      {/* Code Area with Line Numbers */}
      <div
        style={{
          maxHeight: '440px',
          overflowY: 'auto',
          overflowX: wrap ? 'hidden' : 'auto',
          fontFamily: "'JetBrains Mono', 'Fira Code', Menlo, Monaco, Consolas, monospace",
          fontSize: '12px',
          lineHeight: '1.6',
          padding: '12px 0',
        }}
      >
        {lines.length === 0 ? (
          <div style={{ padding: '16px 20px', color: '#64748B', fontStyle: 'italic' }}>
            — Empty payload content —
          </div>
        ) : (
          lines.map((line, idx) => {
            const isMatch = search.trim() && line.toLowerCase().includes(search.toLowerCase());
            return (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  background: isMatch ? 'rgba(56, 189, 248, 0.12)' : 'transparent',
                  padding: '0 14px 0 0',
                }}
              >
                {/* Line number gutter */}
                <div
                  style={{
                    width: '44px',
                    textAlign: 'right',
                    paddingRight: '12px',
                    color: '#475569',
                    userSelect: 'none',
                    flexShrink: 0,
                    fontSize: '11px',
                  }}
                >
                  {idx + 1}
                </div>
                {/* Line content */}
                <div
                  style={{
                    flex: 1,
                    whiteSpace: wrap ? 'pre-wrap' : 'pre',
                    wordBreak: wrap ? 'break-word' : 'normal',
                  }}
                >
                  {renderTokenizedLine(line)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 2. FORMATTED PROMPT VIEWER (Deconstructs roleplay contracts, evaluation context, etc.)
// ─────────────────────────────────────────────────────────────────────────────
interface FormattedPromptViewerProps {
  prompt: string;
  purpose: string;
  scenarioTitle?: string;
}

const FormattedPromptViewer: React.FC<FormattedPromptViewerProps> = ({ prompt, purpose, scenarioTitle }) => {
  const parsedSections = useMemo(() => {
    if (!prompt) return null;
    const text = prompt.trim();

    // 1. First-Person Roleplay Contract Parsing (Simulation Dialogue)
    if (text.includes('### STRICT FIRST-PERSON ROLEPLAY CONTRACT') || text.includes('YOU ARE:') || text.includes('TARGET SPEECH-ACT TO PERFORM:')) {
      const personaMatch = text.match(/YOU ARE:\s*(.*?)(?=\nYOUR ROLE:|$)/s);
      const roleMatch = text.match(/YOUR ROLE:\s*(.*?)(?=\nTARGET SPEECH-ACT:|\nFACTS:|\nFACTS YOU MAY SHARE|$)/s);
      const speechActMatch = text.match(/TARGET SPEECH-ACT TO PERFORM:\s*(.*?)(?=\nFACTS:|\nFACTS YOU MAY SHARE|\nSPEECH-ACT GUIDANCE|$)/s);
      const factsMatch = text.match(/FACTS YOU MAY SHARE \/ DISCLOSE:\s*(.*?)(?=\nSPEECH-ACT GUIDANCE:|\nMANDATORY ROLEPLAY|$)/s);
      const guidanceMatch = text.match(/SPEECH-ACT GUIDANCE:\s*(.*?)(?=\nMANDATORY ROLEPLAY INVARIANTS:|$)/s);
      const dialogueStateMatch = text.match(/### AUTHORITATIVE APPLICATION DIALOGUE STATE:\s*(.*?)$/s);

      // Check if invariants or rules block exists
      const invariantsMatch = text.match(/MANDATORY ROLEPLAY INVARIANTS:\s*(.*?)(?=### AUTHORITATIVE APPLICATION DIALOGUE STATE|$)/s);

      return {
        type: 'ROLEPLAY_CONTRACT' as const,
        persona: personaMatch?.[1]?.trim() || '',
        role: roleMatch?.[1]?.trim() || '',
        speechAct: speechActMatch?.[1]?.trim() || '',
        facts: factsMatch?.[1]?.trim() || '',
        guidance: guidanceMatch?.[1]?.trim() || '',
        dialogueState: dialogueStateMatch?.[1]?.trim() || '',
        invariants: invariantsMatch?.[1]?.trim() || '',
      };
    }

    // 2. Evaluation Blueprint & Transcript Parsing
    if (text.includes('[EVALUATION PROMPT & CONTEXT]')) {
      const parts = text.split('[TRANSCRIPT TURNS');
      const blueprint = parts[0].replace('[EVALUATION PROMPT & CONTEXT]', '').trim();
      let transcript = '';
      let directives = '';

      if (parts[1]) {
        const transcriptAndDirectives = parts[1].split('[COACHING DIRECTIVES]:');
        transcript = transcriptAndDirectives[0].replace(/^\]:?/, '').trim();
        if (transcriptAndDirectives[1]) {
          directives = transcriptAndDirectives[1].trim();
        }
      }

      // Parse transcript lines into turns
      const transcriptTurns = transcript
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .map((line) => {
          const match = line.match(/^(?:Turn \d+:\s*)?\[(.*?)\]:\s*(.*)$/);
          if (match) {
            return { speaker: match[1], text: match[2] };
          }
          return { speaker: '', text: line };
        });

      return {
        type: 'EVALUATION_BLUEPRINT' as const,
        blueprint,
        transcriptTurns,
        rawTranscript: transcript,
        directives,
      };
    }

    // 3. System Instruction & User Turns
    if (text.includes('[SYSTEM INSTRUCTION:') || text.includes('[SYSTEM INSTRUCTION]')) {
      const match = text.match(/\[SYSTEM INSTRUCTION:?(.*?)\]/s);
      const remaining = match ? text.replace(match[0], '').trim() : text;
      return {
        type: 'SYSTEM_AND_USER' as const,
        systemInstruction: match ? match[1].trim() : '',
        userInput: remaining,
      };
    }

    // 4. JSON Prompt
    if (text.startsWith('{') || text.startsWith('[')) {
      try {
        const parsed = JSON.parse(text);
        return {
          type: 'JSON_PROMPT' as const,
          data: parsed,
        };
      } catch {
        // fall through
      }
    }

    return null;
  }, [prompt]);

  if (!parsedSections) {
    return (
      <div style={{ display: 'grid', gap: '10px' }}>
        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={{ fontSize: '16px' }}>📝</span>
            <strong style={{ fontSize: '13.5px', color: 'var(--navy)' }}>
              Standard Prompt Directive ({purpose ? purpose.replace(/_/g, ' ') : 'General'})
            </strong>
          </div>
          <p style={{ margin: 0, fontSize: '13px', color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {prompt}
          </p>
        </div>
      </div>
    );
  }

  // Render Roleplay Contract
  if (parsedSections.type === 'ROLEPLAY_CONTRACT') {
    return (
      <div style={{ display: 'grid', gap: '12px' }}>
        {/* Persona & Role Identity Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
            border: '1px solid #BFDBFE',
            borderRadius: '12px',
            padding: '16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '14px',
          }}
        >
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: '#1D4ED8',
              color: '#FFFFFF',
              display: 'grid',
              placeItems: 'center',
              fontSize: '20px',
              flexShrink: 0,
            }}
          >
            🎭
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: '#1E40AF', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {purpose ? `${purpose.replace(/_/g, ' ')} • ` : ''}AI Persona Contract
              </span>
              {scenarioTitle && (
                <span style={{ fontSize: '11px', background: '#FFFFFF', color: '#1E40AF', padding: '1px 8px', borderRadius: '4px', border: '1px solid #BFDBFE' }}>
                  {scenarioTitle}
                </span>
              )}
            </div>
            <h4 style={{ margin: '4px 0 2px', fontSize: '16px', fontWeight: 800, color: '#0F172A' }}>
              {parsedSections.persona || 'Interactive Simulation Persona'}
            </h4>
            <p style={{ margin: 0, fontSize: '12.5px', color: '#334155', lineHeight: 1.5 }}>
              {parsedSections.role}
            </p>
          </div>
        </div>

        {/* Target Speech Act */}
        {parsedSections.speechAct && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontSize: '15px' }}>🎯</span>
              <strong style={{ fontSize: '13px', color: '#0369A1' }}>Target Speech-Act To Perform</strong>
            </div>
            <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: '6px', padding: '10px 14px', fontSize: '12.5px', color: '#0C4A6E', fontWeight: 600 }}>
              {parsedSections.speechAct}
            </div>
          </div>
        )}

        {/* Dialogue State Card */}
        {parsedSections.dialogueState && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontSize: '15px' }}>⏱️</span>
              <strong style={{ fontSize: '13px', color: '#15803D' }}>Authoritative Dialogue State</strong>
            </div>
            <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '6px', padding: '10px 14px', fontSize: '12.5px', color: '#14532D', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
              {parsedSections.dialogueState}
            </div>
          </div>
        )}

        {/* Allowed Facts & Speech Guidance Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
          {parsedSections.facts && (
            <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span style={{ fontSize: '15px' }}>📌</span>
                <strong style={{ fontSize: '13px', color: '#334155' }}>Facts Allowed To Disclose</strong>
              </div>
              <div style={{ fontSize: '12.5px', color: '#475569', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {parsedSections.facts}
              </div>
            </div>
          )}

          {parsedSections.guidance && (
            <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span style={{ fontSize: '15px' }}>💡</span>
                <strong style={{ fontSize: '13px', color: '#334155' }}>Speech-Act Guidance</strong>
              </div>
              <div style={{ fontSize: '12.5px', color: '#475569', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {parsedSections.guidance}
              </div>
            </div>
          )}
        </div>

        {/* Invariants & Guardrails */}
        {parsedSections.invariants && (
          <details style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '12px 14px' }}>
            <summary style={{ fontSize: '12.5px', fontWeight: 600, color: '#64748B', cursor: 'pointer' }}>
              🛡️ Mandatory Roleplay Invariants &amp; Safety Guardrails (Click to expand)
            </summary>
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#475569', background: '#F8FAFC', padding: '10px 12px', borderRadius: '6px', whiteSpace: 'pre-wrap' }}>
              {parsedSections.invariants}
            </div>
          </details>
        )}
      </div>
    );
  }

  // Render Evaluation Blueprint
  if (parsedSections.type === 'EVALUATION_BLUEPRINT') {
    return (
      <div style={{ display: 'grid', gap: '14px' }}>
        {/* Blueprint Overview */}
        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={{ fontSize: '16px' }}>📋</span>
            <strong style={{ fontSize: '13.5px', color: 'var(--navy)' }}>Evaluation Rubric Blueprint &amp; Criteria</strong>
          </div>
          <div style={{ fontSize: '12.5px', color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {parsedSections.blueprint}
          </div>
        </div>

        {/* Transcript Evaluated */}
        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '16px' }}>💬</span>
              <strong style={{ fontSize: '13.5px', color: 'var(--navy)' }}>Transcript Turns Sent to Evaluator</strong>
            </div>
            <span style={{ fontSize: '11px', color: '#64748B', background: '#F1F5F9', padding: '2px 8px', borderRadius: '4px' }}>
              {parsedSections.transcriptTurns.length} speech turns
            </span>
          </div>

          <div style={{ display: 'grid', gap: '8px' }}>
            {parsedSections.transcriptTurns.map((turn, idx) => {
              const isLearner = turn.speaker.toLowerCase().includes('learner') || turn.speaker.toLowerCase().includes('agent');
              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'flex-start',
                    background: isLearner ? '#EFF6FF' : '#F8FAFC',
                    border: isLearner ? '1px solid #BFDBFE' : '1px solid #E2E8F0',
                    borderRadius: '8px',
                    padding: '10px 12px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      color: isLearner ? '#1D4ED8' : '#475569',
                      background: isLearner ? '#DBEAFE' : '#E2E8F0',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {turn.speaker || `Turn ${idx + 1}`}
                  </span>
                  <div style={{ fontSize: '12.5px', color: isLearner ? '#1E3A8A' : '#1E293B', lineHeight: 1.5, flex: 1 }}>
                    {turn.text}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Coaching Directives */}
        {parsedSections.directives && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontSize: '16px' }}>🎯</span>
              <strong style={{ fontSize: '13.5px', color: '#166534' }}>Coaching &amp; Scoring Directives</strong>
            </div>
            <div style={{ fontSize: '12.5px', color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {parsedSections.directives}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Render System & User Instructions
  if (parsedSections.type === 'SYSTEM_AND_USER') {
    return (
      <div style={{ display: 'grid', gap: '12px' }}>
        {parsedSections.systemInstruction && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontSize: '15px' }}>⚙️</span>
              <strong style={{ fontSize: '13px', color: 'var(--navy)' }}>System Instruction &amp; Context</strong>
            </div>
            <div style={{ fontSize: '12.5px', color: '#334155', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {parsedSections.systemInstruction}
            </div>
          </div>
        )}

        {parsedSections.userInput && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontSize: '15px' }}>📥</span>
              <strong style={{ fontSize: '13px', color: '#1D4ED8' }}>User / Context Input</strong>
            </div>
            <div style={{ fontSize: '12.5px', color: '#1E293B', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {parsedSections.userInput}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Render JSON Prompt
  if (parsedSections.type === 'JSON_PROMPT') {
    return (
      <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '16px' }}>
        <strong style={{ fontSize: '13.5px', color: 'var(--navy)', display: 'block', marginBottom: '10px' }}>
          Structured Input Parameters (JSON)
        </strong>
        <SyntaxCodeViewer code={prompt} isJson={true} title="Input JSON Parameters" />
      </div>
    );
  }

  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// 3. FORMATTED RESPONSE VIEWER (Evaluations, Competencies, Dialogue Speech)
// ─────────────────────────────────────────────────────────────────────────────
interface FormattedResponseViewerProps {
  response: string;
  purpose: string;
  parsedJson: any;
  provider: string;
  model: string;
}

const FormattedResponseViewer: React.FC<FormattedResponseViewerProps> = ({
  response,
  purpose,
  parsedJson,
  provider,
  model,
}) => {
  // Check if dialogue has %%END%% token
  const dialogueEndCheck = useMemo(() => {
    if (!response) return { cleanText: '', hasEndToken: false };
    const hasEndToken = response.includes('%%END%%') || response.includes('[SCENARIO_COMPLETE]');
    const cleanText = response.replace(/%%END%%/g, '').replace(/\[SCENARIO_COMPLETE\]/g, '').trim();
    return { cleanText, hasEndToken };
  }, [response]);

  // Case A: Structured Evaluation Response
  if (parsedJson && (parsedJson.overallScore !== undefined || parsedJson.competencyScores || parsedJson.rubrics || parsedJson.coaching)) {
    const scoreVal = parsedJson.overallScore ?? parsedJson.score;
    const isPassed = parsedJson.isPassed ?? (scoreVal >= 70);
    const competencies: Array<any> = Array.isArray(parsedJson.competencyScores)
      ? parsedJson.competencyScores
      : Array.isArray(parsedJson.criteria)
        ? parsedJson.criteria
        : [];

    const coaching = parsedJson.coaching || {};
    const strengths: string[] = Array.isArray(coaching.strengths) ? coaching.strengths : [];
    const improvements: Array<any> = Array.isArray(coaching.improvements) ? coaching.improvements : [];
    const scopeForImprovement: Array<any> = Array.isArray(coaching.scopeForImprovement) ? coaching.scopeForImprovement : [];
    const actionableTips: string[] = Array.isArray(coaching.actionableTips) ? coaching.actionableTips : [];
    const keyMoments: Array<any> = Array.isArray(parsedJson.keyMoments) ? parsedJson.keyMoments : [];
    const traceability = parsedJson.scoringTraceability;

    return (
      <div style={{ display: 'grid', gap: '14px' }}>
        {/* Score & Verdict Banner */}
        <div
          style={{
            background: isPassed ? 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' : 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)',
            border: isPassed ? '1px solid #BBF7D0' : '1px solid #FECACA',
            borderRadius: '12px',
            padding: '18px 22px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <div>
            <span style={{ fontSize: '11.5px', fontWeight: 800, color: isPassed ? '#166534' : '#991B1B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Synthesized Evaluation Outcome
            </span>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '2px' }}>
              <span style={{ fontSize: '36px', fontWeight: 900, color: isPassed ? '#15803D' : '#B91C1C' }}>
                {scoreVal !== undefined ? `${scoreVal}%` : 'Scored'}
              </span>
              <span style={{ fontSize: '13px', color: isPassed ? '#166534' : '#991B1B' }}>
                Overall Scenario Performance
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span
              style={{
                background: isPassed ? '#15803D' : '#DC2626',
                color: '#FFFFFF',
                padding: '6px 16px',
                borderRadius: '24px',
                fontWeight: 800,
                fontSize: '13px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              }}
            >
              {isPassed ? '✓ Passed Scenario' : '⚠️ Needs Improvement'}
            </span>
            {parsedJson.status && (
              <span style={{ fontSize: '11px', color: '#64748B', background: '#FFFFFF', padding: '4px 8px', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
                Status: {parsedJson.status}
              </span>
            )}
          </div>
        </div>

        {/* Scoring Traceability Card */}
        {traceability && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <strong style={{ fontSize: '13.5px', color: 'var(--navy)' }}>
                  📐 Score Calculation &amp; Weight Traceability
                </strong>
                <div style={{ fontSize: '11.5px', color: 'var(--muted)', marginTop: '2px' }}>
                  Formula: <code style={{ color: 'var(--blue)' }}>{traceability.formula || 'Σ(weight × (score / maxScore)) × 100'}</code>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '12px', color: 'var(--muted)' }}>Pass Mark: <strong>{traceability.passMark}%</strong></span>
              </div>
            </div>

            {Array.isArray(traceability.competencies) && traceability.competencies.length > 0 && (
              <div style={{ overflowX: 'auto', marginBottom: '10px' }}>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #E2E8F0', color: 'var(--muted)' }}>
                      <th style={{ padding: '6px 8px' }}>Competency</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center' }}>Raw Score</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center' }}>Weight</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Contribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {traceability.competencies.map((c: any, cIdx: number) => (
                      <tr key={cIdx} style={{ borderBottom: '1px solid #F1F5F9' }}>
                        <td style={{ padding: '6px 8px', fontWeight: 600, color: 'var(--navy)' }}>{c.name}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center', color: 'var(--ink)' }}>{c.score} / {c.maxScore}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center', color: 'var(--muted)' }}>{c.weight}%</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--blue)' }}>{Number(c.contribution).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12.5px', borderTop: '1px solid #E2E8F0', paddingTop: '10px', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <span style={{ color: 'var(--muted)' }}>Final Weighted Score: </span>
                <strong style={{ fontSize: '14px', color: 'var(--navy)' }}>{traceability.overallScore}%</strong>
              </div>
              {traceability.decisionRationale && (
                <span style={{ fontSize: '12px', fontWeight: 600, color: traceability.isPassed ? '#15803D' : '#B91C1C' }}>
                  {traceability.decisionRationale}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Critical Miss Status Card */}
        {(parsedJson.criticalMissReason || parsedJson.missedCriticalStep || parsedJson.missedCriticalCriterion !== undefined) && (
          <div
            style={{
              background: (parsedJson.missedCriticalStep || parsedJson.missedCriticalCriterion) ? '#FEF2F2' : '#F0FDF4',
              border: (parsedJson.missedCriticalStep || parsedJson.missedCriticalCriterion) ? '1px solid #FECACA' : '1px solid #BBF7D0',
              borderRadius: '10px',
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
            }}
          >
            <span style={{ fontSize: '16px' }}>
              {(parsedJson.missedCriticalStep || parsedJson.missedCriticalCriterion) ? '🚫' : '🛡️'}
            </span>
            <div>
              <strong
                style={{
                  fontSize: '13px',
                  color: (parsedJson.missedCriticalStep || parsedJson.missedCriticalCriterion) ? '#991B1B' : '#15803D',
                  display: 'block',
                  marginBottom: '2px',
                }}
              >
                Critical Requirements &amp; Policy Compliance
              </strong>
              <p
                style={{
                  margin: 0,
                  fontSize: '12px',
                  color: (parsedJson.missedCriticalStep || parsedJson.missedCriticalCriterion) ? '#7F1D1D' : '#166534',
                  lineHeight: 1.5,
                }}
              >
                {parsedJson.criticalMissReason
                  ? parsedJson.criticalMissReason
                  : (parsedJson.missedCriticalStep || parsedJson.missedCriticalCriterion)
                  ? 'A mandatory critical compliance or verification requirement was missed.'
                  : 'All critical verification and regulatory failure gates verified. No critical miss penalty applied.'}
              </p>
            </div>
          </div>
        )}

        {/* Executive Coaching Summary */}
        {(coaching.overallFeedback || coaching.summary || parsedJson.overallFeedback || parsedJson.summary) && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '16px' }}>
            <strong style={{ fontSize: '13.5px', color: 'var(--navy)', display: 'block', marginBottom: '8px' }}>
              Executive Coaching Summary
            </strong>
            <p style={{ margin: 0, fontSize: '13px', color: '#334155', lineHeight: 1.6 }}>
              {coaching.overallFeedback || coaching.summary || parsedJson.overallFeedback || parsedJson.summary}
            </p>
          </div>
        )}

        {/* Competencies Breakdown (Array) */}
        {competencies.length > 0 && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <strong style={{ fontSize: '13.5px', color: 'var(--navy)' }}>
                Evaluated Competency Breakdown ({competencies.length})
              </strong>
              <span style={{ fontSize: '11.5px', color: 'var(--muted)' }}>
                Scale 1.0 – 5.0 with Behavioral Evidence
              </span>
            </div>

            <div style={{ display: 'grid', gap: '10px' }}>
              {competencies.map((comp, idx) => {
                const rawScore = comp.score ?? comp.rating ?? 0;
                const scoreOutOf5 = typeof rawScore === 'number' ? (rawScore > 10 ? (rawScore / 20) : rawScore) : 0;
                const percentage = Math.min(100, Math.round((scoreOutOf5 / 5) * 100));
                const evidenceStatus = comp.evidenceStatus || (scoreOutOf5 >= 4 ? 'DEMONSTRATED' : scoreOutOf5 >= 2.5 ? 'PARTIAL' : 'MISSING');

                return (
                  <div
                    key={idx}
                    style={{
                      padding: '12px 14px',
                      background: '#F8FAFC',
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                      <div>
                        <strong style={{ fontSize: '13px', color: '#0F172A' }}>
                          {comp.name || comp.competencyCode || `Competency #${idx + 1}`}
                        </strong>
                        {comp.competencyCode && comp.name && (
                          <span style={{ marginLeft: '6px', fontSize: '11px', color: '#64748B', fontFamily: 'monospace' }}>
                            ({comp.competencyCode})
                          </span>
                        )}
                        {comp.weight !== undefined && (
                          <span style={{ marginLeft: '8px', fontSize: '10.5px', color: 'var(--muted)', background: '#EDF2F7', padding: '1px 6px', borderRadius: '4px' }}>
                            Weight: {comp.weight}%
                          </span>
                        )}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span
                          style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '2px 8px',
                            borderRadius: '4px',
                            background: evidenceStatus === 'DEMONSTRATED' ? '#DCFCE7' : evidenceStatus === 'PARTIAL' ? '#FEF3C7' : '#FEE2E2',
                            color: evidenceStatus === 'DEMONSTRATED' ? '#15803D' : evidenceStatus === 'PARTIAL' ? '#B45309' : '#B91C1C',
                          }}
                        >
                          {evidenceStatus}
                        </span>
                        <strong style={{ fontSize: '14px', color: '#1D4ED8' }}>
                          {scoreOutOf5.toFixed(1)} <span style={{ fontSize: '11px', color: '#64748B', fontWeight: 500 }}>/ 5.0</span>
                        </strong>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div style={{ width: '100%', height: '6px', background: '#E2E8F0', borderRadius: '3px', margin: '6px 0 8px', overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${percentage}%`,
                          height: '100%',
                          background: scoreOutOf5 >= 4 ? '#10B981' : scoreOutOf5 >= 3 ? '#3B82F6' : '#EF4444',
                          borderRadius: '3px',
                        }}
                      />
                    </div>

                    {/* Feedback narrative */}
                    <p style={{ margin: '0 0 6px', fontSize: '12.5px', color: '#334155', lineHeight: 1.5 }}>
                      {comp.rationale || comp.feedback || comp.qualitativeExplanation || comp.comment || '—'}
                    </p>

                    {/* Quote / Evidence */}
                    {(comp.evidenceQuote || comp.quote || comp.evidence) ? (
                      <div style={{ background: '#FFFFFF', border: '1px solid #CBD5E1', borderRadius: '6px', padding: '6px 10px', fontSize: '11.5px', color: '#475569', fontStyle: 'italic', display: 'flex', gap: '6px' }}>
                        <span>💬</span>
                        <span>"{comp.evidenceQuote || comp.quote || comp.evidence}" {comp.turnIndex !== null && comp.turnIndex !== undefined ? `(Turn ${comp.turnIndex})` : ''}</span>
                      </div>
                    ) : scoreOutOf5 === 0 ? (
                      <div style={{ background: '#FEF2F2', borderLeft: '3px solid #EF4444', borderRadius: '4px', padding: '6px 10px', fontSize: '11.5px', color: '#991B1B' }}>
                        ℹ️ <em>Evidence Traceability:</em> {comp.rationale || comp.feedback || 'Criterion not met during conversation.'}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Legacy Rubrics Fallback (Dictionary) */}
        {!competencies.length && parsedJson.rubrics && typeof parsedJson.rubrics === 'object' && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '16px' }}>
            <strong style={{ fontSize: '13.5px', color: 'var(--navy)', display: 'block', marginBottom: '10px' }}>
              Rubric Criteria Breakdown
            </strong>
            <div style={{ display: 'grid', gap: '8px' }}>
              {Object.entries(parsedJson.rubrics).map(([key, val]: [string, any]) => (
                <div key={key} style={{ padding: '10px 12px', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <strong style={{ fontSize: '12.5px', color: '#1E293B' }}>{key}</strong>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#1D4ED8' }}>
                      Score: {val?.score ?? (typeof val === 'number' ? val : '—')} / 5
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: '12px', color: '#475569' }}>
                    {val?.feedback || val?.evidence || (typeof val === 'string' ? val : '')}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Strategic Pillars for Improvement (Deduplicated capability growth) */}
        {scopeForImprovement.length > 0 && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <span style={{ fontSize: '15px' }}>🎯</span>
              <strong style={{ fontSize: '13px', color: 'var(--navy)' }}>Strategic Capability Pillars ({scopeForImprovement.length})</strong>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '10px' }}>
              {scopeForImprovement.map((pillar, pIdx) => (
                <div key={pIdx} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <strong style={{ fontSize: '12.5px', color: 'var(--navy)' }}>{pillar.title || pillar.pillar || `Pillar #${pIdx + 1}`}</strong>
                    {pillar.pillar && <span style={{ fontSize: '10.5px', color: 'var(--blue)', fontWeight: 600 }}>{pillar.pillar}</span>}
                  </div>
                  <p style={{ margin: 0, fontSize: '12px', color: '#475569', lineHeight: 1.5 }}>
                    {pillar.description || (typeof pillar === 'string' ? pillar : '')}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Strengths & Improvements Grid */}
        {(strengths.length > 0 || improvements.length > 0) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '12px' }}>
            {strengths.length > 0 && (
              <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                  <span style={{ fontSize: '15px' }}>🌟</span>
                  <strong style={{ fontSize: '13px', color: '#15803D' }}>Observed Strengths ({strengths.length})</strong>
                </div>
                <div style={{ display: 'grid', gap: '6px' }}>
                  {strengths.map((s, idx) => (
                    <div key={idx} style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '6px', padding: '8px 12px', fontSize: '12px', color: '#14532D', display: 'flex', gap: '8px' }}>
                      <span style={{ color: '#15803D', fontWeight: 700 }}>✓</span>
                      <span>{typeof s === 'string' ? s : (s as any).description || JSON.stringify(s)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {improvements.length > 0 && (
              <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                  <span style={{ fontSize: '15px' }}>📈</span>
                  <strong style={{ fontSize: '13px', color: '#B45309' }}>Opportunities For Growth ({improvements.length})</strong>
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {improvements.map((imp, idx) => (
                    <div key={idx} style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '6px', padding: '10px 12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <strong style={{ fontSize: '12px', color: '#92400E' }}>
                          {imp.title || `Area #${idx + 1}`}
                        </strong>
                        {imp.priority && (
                          <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '4px', background: imp.priority === 'HIGH' ? '#FEE2E2' : '#FEF3C7', color: imp.priority === 'HIGH' ? '#B91C1C' : '#92400E' }}>
                            {imp.priority}
                          </span>
                        )}
                      </div>
                      <p style={{ margin: '0 0 6px', fontSize: '12px', color: '#78350F' }}>
                        {imp.description || (typeof imp === 'string' ? imp : '')}
                      </p>
                      {imp.coachSuggestion && (
                        <div style={{ background: '#FFFFFF', border: '1px solid #FCD34D', borderRadius: '4px', padding: '4px 8px', fontSize: '11.5px', color: '#78350F' }}>
                          💡 <em>Suggestion:</em> {imp.coachSuggestion}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actionable Tips */}
        {actionableTips.length > 0 && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <span style={{ fontSize: '15px' }}>💡</span>
              <strong style={{ fontSize: '13px', color: 'var(--navy)' }}>Actionable Coaching Recommendations</strong>
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12.5px', color: '#334155', lineHeight: 1.6 }}>
              {actionableTips.map((tip, idx) => (
                <li key={idx} style={{ marginBottom: '4px' }}>{tip}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Key Moments */}
        {keyMoments.length > 0 && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <span style={{ fontSize: '15px' }}>📍</span>
              <strong style={{ fontSize: '13px', color: 'var(--navy)' }}>Highlighted Dialogue Moments ({keyMoments.length})</strong>
            </div>
            <div style={{ display: 'grid', gap: '8px' }}>
              {keyMoments.map((km, idx) => (
                <div key={idx} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px 12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <strong style={{ fontSize: '12.5px', color: '#1E293B' }}>{km.title || `Moment #${idx + 1}`}</strong>
                    <span style={{ fontSize: '11px', color: '#64748B', background: '#E2E8F0', padding: '1px 6px', borderRadius: '4px' }}>
                      {km.speaker || 'Learner'}
                    </span>
                  </div>
                  <div style={{ fontStyle: 'italic', fontSize: '12px', color: '#334155', marginBottom: '6px' }}>
                    "{km.quote || km.said}"
                  </div>
                  {km.whyItMatters && (
                    <div style={{ fontSize: '11.5px', color: '#475569', marginBottom: '4px' }}>
                      <strong>Impact:</strong> {km.whyItMatters}
                    </div>
                  )}
                  {km.trySaying && (
                    <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '4px', padding: '4px 8px', fontSize: '11.5px', color: '#1E40AF' }}>
                      💡 <em>Try saying:</em> "{km.trySaying}"
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Case B: Simulation Dialogue Response (Character Spoken Line)
  if (purpose === 'SIMULATION_DIALOGUE' || (!parsedJson && response)) {
    return (
      <div style={{ display: 'grid', gap: '14px' }}>
        {/* Dialogue Balloon Card */}
        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
          <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: '#FFFFFF',
                display: 'grid',
                placeItems: 'center',
                fontSize: '20px',
                flexShrink: 0,
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
              }}
            >
              🤖
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div>
                  <strong style={{ fontSize: '14px', color: '#0F172A' }}>AI Customer / Roleplay Response</strong>
                  <span style={{ marginLeft: '8px', fontSize: '11px', color: '#64748B' }}>
                    Generated by {model} ({provider})
                  </span>
                </div>
                <span style={{ fontSize: '11px', color: '#0F8A5F', background: '#ECFDF5', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>
                  Spoken Turn
                </span>
              </div>

              {/* Spoken Speech Box */}
              <div
                style={{
                  background: '#F0FDF4',
                  border: '1px solid #BBF7D0',
                  borderRadius: '12px',
                  padding: '16px 20px',
                  fontSize: '14.5px',
                  color: '#14532D',
                  lineHeight: 1.6,
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                }}
              >
                "{dialogueEndCheck.cleanText || '— Empty response string received —'}"
              </div>

              {/* End of Scenario Indicator */}
              {dialogueEndCheck.hasEndToken && (
                <div
                  style={{
                    marginTop: '12px',
                    background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
                    border: '1px solid #93C5FD',
                    borderRadius: '8px',
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <span style={{ fontSize: '18px' }}>🏁</span>
                  <div>
                    <strong style={{ fontSize: '12.5px', color: '#1E40AF', display: 'block' }}>
                      Scenario Exit Trigger Emitted (%%END%%)
                    </strong>
                    <span style={{ fontSize: '11.5px', color: '#1E3A8A' }}>
                      The AI persona satisfied its completion condition. The interactive simulation has reached its authoritative conclusion.
                    </span>
                  </div>
                </div>
              )}

              {/* Speech Statistics */}
              <div style={{ display: 'flex', gap: '16px', marginTop: '12px', fontSize: '11.5px', color: '#64748B' }}>
                <span>Length: <strong>{dialogueEndCheck.cleanText.length}</strong> chars</span>
                <span>Word count: <strong>{dialogueEndCheck.cleanText.split(/\s+/).filter(Boolean).length}</strong> words</span>
                <span>Est. speech time: <strong>~{Math.max(1, Math.round(dialogueEndCheck.cleanText.split(/\s+/).filter(Boolean).length / 2.5))}</strong> sec</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Case C: Other JSON responses (Assists, Scenarios, etc.)
  if (parsedJson) {
    return (
      <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '16px' }}>
        <strong style={{ fontSize: '13.5px', color: 'var(--navy)', display: 'block', marginBottom: '10px' }}>
          Structured Output Response (JSON)
        </strong>
        <SyntaxCodeViewer code={response} isJson={true} title="Output Response Data" accentColor="#10B981" />
      </div>
    );
  }

  // Fallback
  return (
    <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '16px' }}>
      <p style={{ margin: 0, fontSize: '13px', color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
        {response || '— No response content —'}
      </p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 4. MAIN INSPECTOR MODAL
// ─────────────────────────────────────────────────────────────────────────────
const InspectorModal: React.FC<InspectorModalProps> = ({
  log,
  onClose,
  onCopy,
  copiedPrompt,
  copiedResponse,
  getPurposeBadge,
}) => {
  const [activeTab, setActiveTab] = useState<'input' | 'prompt' | 'response_formatted' | 'response_raw' | 'telemetry'>('input');
  const [copiedFullJson, setCopiedFullJson] = useState(false);

  const badge = getPurposeBadge(log.purpose);

  // Attempt to parse JSON response if possible
  const parsedJson = useMemo(() => {
    if (!log.response) return null;
    try {
      let trimmed = log.response.trim();
      if (trimmed.startsWith('```')) {
        trimmed = trimmed.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '').trim();
      }
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        return JSON.parse(trimmed);
      }
    } catch {
      // not json
    }
    return null;
  }, [log.response]);

  const handleCopyFullRecord = () => {
    navigator.clipboard.writeText(JSON.stringify(log, null, 2));
    setCopiedFullJson(true);
    setTimeout(() => setCopiedFullJson(false), 2000);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        background: 'rgba(5, 10, 25, 0.85)',
        backdropFilter: 'blur(8px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '1020px',
          maxHeight: '92vh',
          background: '#FFFFFF',
          borderRadius: '16px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #E2E8F0',
            background: 'linear-gradient(135deg, #0B192C 0%, #1E3E62 100%)',
            color: '#FFFFFF',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '18px' }}>🔍</span>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#FFFFFF' }}>
                LLM Execution Audit Inspector: <span style={{ fontFamily: 'monospace', color: '#93C5FD' }}>{log.id}</span>
              </h3>
              <span
                style={{
                  background: badge.bg,
                  color: badge.color,
                  padding: '2px 8px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 700,
                }}
              >
                {badge.icon} {badge.label}
              </span>
            </div>
            <p style={{ margin: '6px 0 0', fontSize: '12.5px', color: '#CBD5E1' }}>
              Model: <strong style={{ color: '#FDE047' }}>{log.model}</strong> ({log.provider}) • Cost: <strong style={{ color: '#86EFAC' }}>${Number(log.estimatedCost ?? 0).toFixed(5)}</strong> • Latency: <strong style={{ color: '#E2E8F0' }}>{Number(log.latencyMs ?? 0)} ms</strong> • Tokens: <strong style={{ color: '#93C5FD' }}>{log.totalTokens ?? (Number(log.promptTokens || 0) + Number(log.completionTokens || 0))}</strong> ({log.promptTokens ?? 0} in / {log.completionTokens ?? 0} out)
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: 0,
              borderRadius: '8px',
              color: '#FFFFFF',
              width: '32px',
              height: '32px',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
              fontSize: '16px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Modal Navigation Tabs */}
        <div
          style={{
            display: 'flex',
            gap: '4px',
            background: '#F1F5F9',
            padding: '8px 24px 0',
            borderBottom: '1px solid #E2E8F0',
            overflowX: 'auto',
          }}
        >
          {[
            { id: 'input', label: '📥 Input Data & Context', desc: 'Parsed Structure' },
            { id: 'prompt', label: '📤 Full Prompt Payload', desc: 'Raw Sent' },
            { id: 'response_formatted', label: '💬 Response from LLM', desc: 'Formatted View' },
            { id: 'response_raw', label: '📦 LLM Response Payload', desc: 'Raw / Completion' },
            { id: 'telemetry', label: '⚡ Observability & Economics', desc: 'Tokens & Cost' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                padding: '10px 16px',
                borderRadius: '8px 8px 0 0',
                border: 'none',
                borderBottom: activeTab === tab.id ? '3px solid #1D4ED8' : '3px solid transparent',
                background: activeTab === tab.id ? '#FFFFFF' : 'transparent',
                color: activeTab === tab.id ? '#1D4ED8' : '#64748B',
                fontWeight: activeTab === tab.id ? 700 : 500,
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s ease',
              }}
            >
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Modal Body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, background: '#F8FAFC' }}>
          {/* Metadata Badges Strip */}
          <div
            style={{
              background: '#FFFFFF',
              border: '1px solid #E2E8F0',
              borderRadius: '10px',
              padding: '12px 16px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '12px',
              fontSize: '12px',
              marginBottom: '16px',
            }}
          >
            <div>
              <span style={{ color: 'var(--muted)', display: 'block' }}>Attempt Reference</span>
              <strong style={{ color: 'var(--navy)', fontFamily: 'monospace' }}>{log.attemptId || 'N/A (Studio/Creation)'}</strong>
            </div>
            <div>
              <span style={{ color: 'var(--muted)', display: 'block' }}>Scenario Reference</span>
              <strong style={{ color: 'var(--navy)' }}>{log.scenarioTitle || log.scenarioId || 'General Blueprint'}</strong>
            </div>
            <div>
              <span style={{ color: 'var(--muted)', display: 'block' }}>User / Learner</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                <div className="llm-actor-avatar" style={{ width: '26px', height: '26px', fontSize: '11px' }}>
                  {getActorInitials(log.actorName, log.actorEmail)}
                </div>
                <div>
                  <strong style={{ color: 'var(--navy)', fontSize: '12.5px' }}>
                    {log.actorName || log.actorEmail || log.actorId}
                  </strong>
                  <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                    {log.actorEmail || log.actorId || ''} • <span style={{ fontWeight: 700, color: 'var(--blue)' }}>{log.actorRole || 'LEARNER'}</span>
                    {log.actorRole && log.actorRole !== 'LEARNER' && (
                      <span style={{ marginLeft: '4px', fontSize: '10px', color: 'var(--muted)' }}>(Participant: Learner)</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div>
              <span style={{ color: 'var(--muted)', display: 'block' }}>Organization</span>
              <strong style={{ color: 'var(--navy)' }}>{log.tenantName || log.tenantId}</strong>
            </div>
            <div>
              <span style={{ color: 'var(--muted)', display: 'block' }}>Pipeline Status</span>
              <span
                className={`ad-res ${log.status === 'SUCCESS' ? 'ad-res-pass' : 'ad-res-fail'}`}
                style={{ fontSize: '11px', padding: '2px 8px', display: 'inline-flex' }}
              >
                <i />
                API {log.status}
              </span>
            </div>
            {(log.learnerOutcome || (log.purpose === 'SIMULATION_EVALUATION' && log.response)) && (
              <div>
                <span style={{ color: 'var(--muted)', display: 'block' }}>Learner Outcome</span>
                <span
                  className={`ad-res ${
                    (log.learnerOutcome === 'PASSED' || (log.response && log.response.includes('"isPassed":true'))) ? 'ad-res-pass' : 'ad-res-fail'
                  }`}
                  style={{ fontSize: '11px', padding: '2px 8px', display: 'inline-flex' }}
                >
                  <i />
                  {(log.learnerOutcome === 'PASSED' || (log.response && log.response.includes('"isPassed":true'))) ? 'PASSED' : 'NEEDS IMPROVEMENT'}
                </span>
              </div>
            )}
          </div>

          {/* TAB 1: Structured Input Data for LLM */}
          {activeTab === 'input' && (
            <div style={{ display: 'grid', gap: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: 'var(--navy)' }}>
                    📥 Input Data &amp; Context Sent to {log.provider}
                  </h4>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--muted)' }}>
                    Parsed breakdown of persona roleplay contract, dialogue state, criteria, or input parameters.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onCopy(log.prompt, 'prompt')}
                  style={{
                    background: copiedPrompt ? '#D1FAE5' : '#FFFFFF',
                    color: copiedPrompt ? '#065F46' : '#1D4ED8',
                    border: '1px solid #CBD5E1',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {copiedPrompt ? '✓ Copied Prompt' : '📋 Copy Prompt'}
                </button>
              </div>

              <FormattedPromptViewer
                prompt={log.prompt}
                purpose={log.purpose}
                scenarioTitle={log.scenarioTitle}
              />
            </div>
          )}

          {/* TAB 2: Full Raw Prompt Payload */}
          {activeTab === 'prompt' && (
            <div style={{ display: 'grid', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: 'var(--navy)' }}>
                    📤 Full Prompt Payload (Over The Wire)
                  </h4>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--muted)' }}>
                    Verbatim raw prompt string and system instruction sent to {log.provider} ({log.model}).
                  </p>
                </div>
              </div>

              <SyntaxCodeViewer
                code={log.prompt || '— No prompt logged —'}
                title={`Raw Prompt — ${log.provider} / ${log.model}`}
                accentColor="#38BDF8"
                onCopy={() => onCopy(log.prompt, 'prompt')}
                copied={copiedPrompt}
              />
            </div>
          )}

          {/* TAB 3: Formatted Response from LLM */}
          {activeTab === 'response_formatted' && (
            <div style={{ display: 'grid', gap: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#0F8A5F' }}>
                    💬 Formatted Response from LLM
                  </h4>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--muted)' }}>
                    Human-readable parsed breakdown of spoken speech, coaching summary, competencies, and key moments.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onCopy(log.response, 'response')}
                  style={{
                    background: copiedResponse ? '#D1FAE5' : '#ECFDF5',
                    color: copiedResponse ? '#065F46' : '#0F8A5F',
                    border: '1px solid #A7F3D0',
                    padding: '6px 14px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {copiedResponse ? '✓ Copied' : '📋 Copy Response'}
                </button>
              </div>

              <FormattedResponseViewer
                response={log.response}
                purpose={log.purpose}
                parsedJson={parsedJson}
                provider={log.provider}
                model={log.model}
              />
            </div>
          )}

          {/* TAB 4: Raw LLM Response & Completion Payload */}
          {activeTab === 'response_raw' && (
            <div style={{ display: 'grid', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#0F8A5F' }}>
                    📦 Raw LLM Response &amp; Completion Payload
                  </h4>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--muted)' }}>
                    Verbatim completion received from {log.provider} ({log.model}).
                  </p>
                </div>
              </div>

              <SyntaxCodeViewer
                code={log.response || '— No completion response logged —'}
                title={`Raw Completion — ${log.provider} / ${log.model}`}
                isJson={parsedJson !== null}
                accentColor="#10B981"
                onCopy={() => onCopy(log.response, 'response')}
                copied={copiedResponse}
              />
            </div>
          )}

          {/* TAB 5: Observability & Economics */}
          {activeTab === 'telemetry' && (
            <div style={{ display: 'grid', gap: '14px' }}>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: 'var(--navy)' }}>
                ⚡ Token Consumption &amp; Economic Telemetry
              </h4>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '14px' }}>
                  <span style={{ fontSize: '11.5px', color: 'var(--muted)', display: 'block' }}>Prompt (Input) Tokens</span>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: '#1D4ED8', marginTop: '4px' }}>
                    {Number(log.promptTokens ?? 0).toLocaleString()}
                  </div>
                </div>

                <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '14px' }}>
                  <span style={{ fontSize: '11.5px', color: 'var(--muted)', display: 'block' }}>Completion (Output) Tokens</span>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: '#0F8A5F', marginTop: '4px' }}>
                    {Number(log.completionTokens ?? 0).toLocaleString()}
                  </div>
                </div>

                <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '14px' }}>
                  <span style={{ fontSize: '11.5px', color: 'var(--muted)', display: 'block' }}>Total Tokens</span>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: '#0F172A', marginTop: '4px' }}>
                    {Number(log.totalTokens ?? (Number(log.promptTokens || 0) + Number(log.completionTokens || 0))).toLocaleString()}
                  </div>
                </div>

                <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '14px' }}>
                  <span style={{ fontSize: '11.5px', color: 'var(--muted)', display: 'block' }}>Calculated Cost (USD)</span>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: '#0F8A5F', marginTop: '4px' }}>
                    ${Number(log.estimatedCost ?? 0).toFixed(6)}
                  </div>
                </div>
              </div>

              <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '10px', padding: '16px' }}>
                <strong style={{ fontSize: '13px', color: 'var(--navy)', display: 'block', marginBottom: '10px' }}>
                  Execution Parameters &amp; Model Tariffs
                </strong>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12.5px' }}>
                  <div><strong>AI Provider:</strong> {log.provider}</div>
                  <div><strong>Model Name:</strong> {log.model}</div>
                  <div><strong>Response Latency:</strong> {Number(log.latencyMs ?? 0)} ms</div>
                  <div><strong>Temperature:</strong> {log.temperature ?? 0.7}</div>
                  <div><strong>Generated At:</strong> {log.createdAt ? (isNaN(new Date(log.createdAt).getTime()) ? String(log.createdAt) : new Date(log.createdAt).toLocaleString()) : '—'}</div>
                  <div><strong>Error Code:</strong> {log.errorCode || 'None (Clean Execution)'}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: '14px 24px',
            borderTop: '1px solid #E2E8F0',
            background: '#FFFFFF',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            type="button"
            onClick={handleCopyFullRecord}
            style={{
              background: copiedFullJson ? '#D1FAE5' : '#F1F5F9',
              color: copiedFullJson ? '#065F46' : '#334155',
              border: '1px solid #CBD5E1',
              padding: '6px 14px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {copiedFullJson ? '✓ Copied Full JSON' : '📦 Copy Complete Audit Record (JSON)'}
          </button>

          <button
            type="button"
            onClick={onClose}
            className="btn primary md"
            style={{ padding: '8px 24px' }}
          >
            Close Inspector
          </button>
        </div>
      </div>
    </div>
  );
};
